/**
 * Feature flag constants.
 *
 * These feature flags are managed in WorkOS and used to control access
 * to various features in the application.
 *
 * Values must match the feature flag names configured in WorkOS.
 */
export const FeatureFlag = {
  // Intake workflow feature flags
  INTAKE_PDF_STAMP: 'intake-pdf-stamp',
  INTAKE_SEE_AI_EXPLANATION: 'intake-see-ai-explanation',
  INTAKE_AI_SCHEDULING: 'intake-ai-scheduling',

  // Medical coding feature flags
  MEDICAL_CODING_ONLY_DOCUMENT_BASED: 'only-document-based-coding',
  // RogerDoc-only (GET /rogerdoc/feature_flags — not configured in WorkOS)
  ALLOW_RERUN_COMPLETED_CODING: 'allow-rerun-completed-coding',
  MEDICAL_CODING_CONSIDER_THERAPY_EVAL: 'medical-coding-consider-therapy-eval',
  MEDICAL_CODING_SHOW_FORCE_CODING_RERUN_BUTTON:
    'medical-coding-show-force-coding-rerun-button',
  MEDICAL_CODING_CHECK_NEW_DOCS_CRON: 'medical_coding_check_new_docs_cron',
  // Reporting feature flags
  ADOPTION_REPORT_BY_BRANCH: 'adoption-report-by-branch',
  ADVANCED_REPORTING: 'advanced-reporting',
  CUSTOM_DASHBOARD: 'custom-dashboard',

  // RogerDoc-only: goals progress tracking feature
  USE_WORKSHEET_GOALS_PROGRESS: 'use_worksheet_goals_progress',

  // Order Manager feature flags
  ORDER_MANAGER: 'order-manager',

  // OASIS Manager feature flags
  OASIS_MANAGER: 'oasis-manager',

  // Live Scrubber QA findings — gates the backend `contradictions` response and
  // the QA tab that renders it.
  CONTRADICTION_RULES_ENABLED: 'contradiction-rules-enabled',

  // Show full roles options in the clinician manager UI
  // For non-EMR users they don't need to see the clinic level roles
  SHOW_CLINIC_INFO_IN_USER_MANAGER: 'show-clinic-info-in-user-manager',
  // If true, the user can see roles not assigned to them in the invite/add user dialogs.
  // When false, only roles already assigned to the current user are shown.
  SHOW_UNASSIGNED_ROLES: 'show-unassigned-roles',

  // Product update modal ("What's New") in the app header
  PRODUCT_UPDATE_MODAL_ENABLED: 'product-update-modal-enabled',

  // Invoice billing: shows the insurance Bill type select. Mirrors
  // the backend FeatureFlag.INVOICE_BILLING gate on writing bill_type INVOICE.
  INVOICE_BILLING: 'invoice-billing',

  // Payroll Manager screens — Unpaid/Paid tabs. One shared flag
  // gates the sidebar item and every /payroll endpoint.
  PAYROLL_MANAGER: 'payroll-manager',

  // Relaxed billing readiness. On: visits other than eval/re-eval
  // and SOC OASIS count as billing-ready at pending clinician signature.
  // Mirrors the backend FeatureFlag.PENDING_SIGNATURE_BILLING_READINESS gate.
  PENDING_SIGNATURE_BILLING_READINESS: 'pending-signature-billing-readiness',

  // On: the Patient Roster picks the payment OASIS by visit date. Off: by M0090.
  // Mirrors the backend FeatureFlag.PAYMENT_OASIS_UPDATED_BEHAVIOR gate.
  PAYMENT_OASIS_UPDATED_BEHAVIOR: 'payment-oasis-updated-behavior',

  // On: no NOA carries an admitting diagnosis, whatever the insurance says.
  // Mirrors the backend FeatureFlag.NOA_NO_ADMITTING_DIAGNOSIS gate.
  NOA_NO_ADMITTING_DIAGNOSIS: 'noa-no-admitting-diagnosis',

  // Roger Chat. The only switch on chat: there is no access slug.
  CHAT: 'chat',

  // Internal Tools' Agency Data Fill tab. The same flag the backend router
  // checks, so the tab is absent for a super user whose organization has not
  // turned the screen on, rather than present and answering 403.
  KINNSER_AGENCY_SYNC_UI: 'kinnser-agency-sync-ui',

  // On-demand External EMR import: census shells, Import buttons and the
  // census refresh. Mirrors the backend gate
  // (require_external_emr_import_agency). Off: nothing new starts and shells
  // are hidden; imports in flight finish and their status still shows.
  EXTERNAL_EMR_IMPORT: 'external-emr-import',

  // Take Over v2: the planned Take Over, its undo and the chart lock. Needs
  // an External EMR as well. Mirrors the backend's take_over_available. Off:
  // the basic Take Over from Add Episode, as on main.
  EXTERNAL_EMR_TAKE_OVER: 'external-emr-take-over',
} as const;

export const PermissionSlug = {
  NOA_MANAGER: 'noa-manager:view',
  // One permission, not two. Reading the inbox without being able to act on
  // it cannot do the job the screen exists for.
  FAX_INBOX_MANAGE: 'fax-inbox:manage',
  // Preparing a port and submitting it are separate grants: submitting moves
  // a live number away from the agency's current carrier and cannot be undone.
  FAX_NUMBER_PORTS_EDIT: 'fax-number-ports:edit',
  FAX_NUMBER_PORTS_SEND: 'fax-number-ports:send',
  GOALS_ADMIN_TAB: 'goals:admin-tab',
  ORDER_MANAGER: 'order-manager:view',
  ORDERS_WAIVE_EVAL: 'orders:waive-eval',
  OASIS_MANAGER: 'oasis-manager:view',
  // Mirrors the backend OASIS_SEND_SHP_CHECK gate on the SHP check route.
  OASIS_SEND_SHP_CHECK: 'oasis:send-shp-check',
  CAHPS_MANAGER: 'cahps-manager:view',
  IMPORTER_ACCESS: 'importer:access',
  // Start, retry or cancel an on-demand External EMR chart import (intake or
  // Patient Manager). Agency-scoped; importer:access stays the bulk console.
  CHART_IMPORT_START: 'chart-import:start',
  // Take Over: move an imported chart to Roger, choosing which claim periods
  // the External EMR keeps. Mirrors the backend EPISODE_TAKE_OVER gate.
  EPISODE_TAKE_OVER: 'episode:take-over',
  PATIENT_MANAGER: 'patient-mgr:access',
  CLINICIAN_MANAGER: 'clinician-mgr:access',
  // Merge a duplicate account into the one its owner uses. Mirrors the
  // backend USERS_MERGE gate on the User Manager merge routes.
  USERS_MERGE: 'users:merge',
  DEBUG_ACCESS: 'debug:access',
  REPORTS_ACCESS: 'reports:access',
  ROGER_EMR_REPORTS_ACCESS: 'roger-emr-reports:access',
  REPORTS_VISITS: 'reports:visits',
  REPORTS_EPISODES: 'reports:episodes',
  REPORTS_PATIENTS: 'reports:patients',
  REPORTS_CLAIMS: 'reports:claims',
  // Staff licenses reporting dataset. Independent of user-licenses:view:
  // the report builder never consults the CRUD gate.
  REPORTS_USER_LICENSES: 'reports:user_licenses',
  BILLING_ACCESS: 'billing:view',
  BILLING_EXPORT_CLAIMS: 'billing:export:claims',
  BILLING_EXPORT_REMITTANCES: 'billing:export:remittances',
  BILLING_EXPORT_NOAS: 'billing:export:noas',
  // Send claims outward (submit / send-again / create or abandon a
  // replacement draft). Mirrors the backend CLAIMS_SUBMIT gates on the
  // submission and replacement routes.
  CLAIMS_SUBMIT: 'claims:submit',
  // Bill past a payment-OASIS export hold. Mirrors the backend
  // CLAIMS_OVERRIDE_OASIS_HOLD gate on the payment-oasis-hold override route.
  CLAIMS_OVERRIDE_OASIS_HOLD: 'claims:override-oasis-hold',
  // Send answers to a Stedi enrollment task from Admin > Enrollments. Mirrors
  // the backend ENROLLMENT_SUBMIT gate on the task-complete endpoint.
  ENROLLMENT_SUBMIT: 'enrollment:submit',
  // Attach unmatched 277CA / 835 evidence to a claim version.
  // Mirrors the backend CLAIMS_RECONCILE gate on the reconciliation routes.
  CLAIMS_RECONCILE: 'claims:reconcile',
  QA_ACCESS: 'qa:access',
  // The QA side of chart signing. Mirrors the backend gates in
  // task_signing; being selectable as a Case Manager grants none of them.
  TASKS_APPROVE: 'tasks:approve',
  TASKS_RETURN_TO_CLINICIAN: 'tasks:return-to-clinician',
  TASKS_DELETE_IMPORTED: 'tasks:delete-imported',
  TASKS_BYPASS_CLINICIAN_SIGNATURE: 'tasks:bypass-clinician-signature',
  TASKS_EDIT_OUTSIDE_RECORD: 'tasks:edit-outside-record',
  TASKS_REOPEN: 'tasks:reopen',
  // The Hotbox QA scope: every chart waiting on QA, not only the caller's.
  QA_QUEUE_VIEW: 'qa-queue:view',
  HOTBOX_ACCESS: 'hotbox:access',
  HOTBOX_RESCHEDULE: 'hotbox:reschedule',
  EVV_CAPTURE: 'evv:capture',
  EVV_VIEW: 'evv:view',
  EVV_REMOVE: 'evv:remove',
  ADMIN_SUPER_USER: 'admin:super-user',
  EXTERNAL_ADMIN_ACCESS: 'external-admin:access',
  INTAKE_ACCESS: 'intake:access',
  INTAKE_EXPORT: 'intake:export',
  INTAKE_COLUMNS_EDIT: 'intake-columns:edit',
  ELIGIBILITY_CHECK: 'eligibility:check',
  CODING_ACCESS: 'coding:access',
  CODING_FORCE_RERUN: 'coding:force-rerun',
  ROGERDOC_DASHBOARD_MANAGE: 'rogerdoc-dashboard:manage',
  CUSTOMIZATIONS_ACCESS: 'customizations:access',
  EMR_MANAGE: 'emr:manage',
  PATIENT_INFO_EDIT: 'patient-info:edit',
  PAYROLL_ACCESS: 'payroll:access',
  // Work log: the Actions-menu dialog on every task (visit, order, communication,
  // misc task) and the Worklog report. The report also needs
  // ROGER_EMR_REPORTS_ACCESS; this slug alone only opens the task dialog.
  WORKLOG_VIEW: 'worklog:view',
  HISTORICAL_CLAIMS_VIEW: 'historical-claims:view',
  // Amend clinical records inside a closed certification period.
  // Without it every write on a closed period is refused; the chart reads the
  // slug only to say so before the save, never as the gate.
  EPISODE_AMEND_CLOSED: 'episode:amend-closed',
  // Mark incomplete (reopen) a completed episode, and Mark completed
  // a reopened one. The backend gates both routes; the UI only hides them.
  EPISODE_REOPEN: 'episode:reopen',
  // Continue goals from the previous episode on the Goals tab. The
  // backend gates the routes; the chart only hides the button.
  GOALS_CARRY_FORWARD: 'goals:carry-forward',
  // Sees each clinician's scheduling-point load beside their name when
  // assigning work. Separate from the gate that authors those values.
  SCHEDULING_POINTS_VIEW_LOAD: 'scheduling-points:view-load',
  // Staff license tracking. Reading a colleague's credential records, changing
  // them, and authoring the agency's list of credential kinds are three
  // separate acts on two separate screens.
  USER_LICENSES_VIEW: 'user-licenses:view',
  USER_LICENSES_EDIT: 'user-licenses:edit',
  USER_LICENSE_TYPES_EDIT: 'user-license-types:edit',
  // One view/edit pair per Admin-tab section, replacing the coarse
  // external-admin:access / emr:manage gate that every section inherited.
  CLINIC_INFO_VIEW: 'clinic-info:view',
  CLINIC_INFO_EDIT: 'clinic-info:edit',
  AGENCY_INSURANCE_VIEW: 'agency-insurance:view',
  AGENCY_INSURANCE_EDIT: 'agency-insurance:edit',
  CLINIC_PHYSICIANS_VIEW: 'clinic-physicians:view',
  CLINIC_PHYSICIANS_EDIT: 'clinic-physicians:edit',
  REFERRAL_SOURCES_VIEW: 'referral-sources:view',
  REFERRAL_SOURCES_EDIT: 'referral-sources:edit',
  CHART_WORKFLOW_VIEW: 'chart-workflow:view',
  CHART_WORKFLOW_EDIT: 'chart-workflow:edit',
  PATIENT_CREATION_SETTINGS_VIEW: 'patient-creation-settings:view',
  PATIENT_CREATION_SETTINGS_EDIT: 'patient-creation-settings:edit',
  // Distinct from SCHEDULING_POINTS_VIEW_LOAD above: that one shows a
  // clinician's accumulated load when assigning work, these gate the table of
  // point values itself.
  SCHEDULING_POINTS_VIEW_CONFIG: 'scheduling-points:view-config',
  SCHEDULING_POINTS_EDIT_CONFIG: 'scheduling-points:edit-config',
  CHART_EDIT_UNASSIGNED: 'chart:edit-unassigned',
  CHART_EDIT_UNASSIGNED_AGENCY: 'chart:edit-unassigned-agency',
  EPISODE_MANAGER_EDIT_UNASSIGNED: 'episode-manager:edit-unassigned',
  EPISODE_MANAGER_EDIT_UNASSIGNED_AGENCY:
    'episode-manager:edit-unassigned-agency',
  HHRG_COSTS_VIEW: 'hhrg-costs:view',
  HHRG_COSTS_EDIT: 'hhrg-costs:edit',
  // Authoring the agency's library of standardized text for visit-form text
  // boxes. Only the Admin section is gated; the visit form's own read of the
  // library is authenticated rather than permissioned, so no slug here has
  // anything to do with whether a clinician sees a template.
  FORM_TEMPLATES_VIEW: 'form-templates:view',
  FORM_TEMPLATES_EDIT: 'form-templates:edit',
  // Task Details, the Actions-menu panel on every task. View shows the menu
  // item; edit unlocks the fields inside it. The server gates the panel's own
  // routes on the same two slugs.
  TASK_DETAILS_VIEW: 'task-details:view',
  TASK_DETAILS_EDIT: 'task-details:edit',
  // Which task types this agency treats as paperwork rather than covered
  // care. The edit slug decides what reaches a claim, a LUPA count, an
  // authorization budget and a payroll sheet, so it is separate from the
  // scheduling-point values next door.
  TASK_TYPES_VIEW: 'task-types:view',
  TASK_TYPES_EDIT: 'task-types:edit',
  // Which visit statuses Payroll Manager lists as unpaid and lets users mark
  // paid. The server also lets payroll:access and emr:manage read
  // the setting, for the Unpaid tab; these two open the Admin section.
  PAYROLL_SETTINGS_VIEW: 'payroll-settings:view',
  PAYROLL_SETTINGS_EDIT: 'payroll-settings:edit',
  // Agency print name (Admin → Agency).
  AGENCY_PRINT_NAME_VIEW: 'agency-print-name:view',
  AGENCY_PRINT_NAME_EDIT: 'agency-print-name:edit',
  // Chat group administration (the Group Manager). The UI mirrors the
  // server gate; `chat:access` does not exist.
  CHAT_EDIT_GROUPS: 'chat:edit-groups',
  CHAT_SEND_ANNOUNCEMENT: 'chat:send-announcement',
} as const;

/**
 * Any one of these reads the External EMR census and its counts. Mirrors the
 * server's `EXTERNAL_EMR_READ_PERMISSIONS`.
 */
export const EXTERNAL_EMR_READ_PERMISSIONS = [
  PermissionSlug.PATIENT_MANAGER,
  PermissionSlug.EMR_MANAGE,
  PermissionSlug.CHART_IMPORT_START,
] as const;

export type FeatureFlagType = (typeof FeatureFlag)[keyof typeof FeatureFlag];
